import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts';
import { RUBRIC_DIMENSIONS, RUBRIC_MAX_SCORE } from '../../constants/rubricDimensions';
import {
  CATEGORICAL_LIGHT,
  CHART_INK,
  WEAK_AREA_THRESHOLD
} from '../../constants/chartPalette';

export interface TrendPoint {
  date: string | null;
  title: string | null;
  dimensions: Record<string, number | null>;
}

interface DimensionTrendGridProps {
  /** Sessions oldest-first. */
  history: TrendPoint[];
}

/** The soft-call line on the rubric's native 1-5 scale. */
const THRESHOLD_SCORE = (WEAK_AREA_THRESHOLD / 100) * RUBRIC_MAX_SCORE;

/** Single hue: each panel is ONE series, so colour carries no identity here. */
const SERIES_COLOR = CATEGORICAL_LIGHT[0];

/**
 * All ten dimensions over time, as small multiples.
 *
 * WHY TEN PANELS AND NOT TEN LINES. The obvious build is one chart with a line
 * per dimension. It is unreadable: ten series need ten distinguishable hues,
 * and no palette provides them — beyond about eight, adjacent colours stop
 * separating under normal vision, let alone colour-vision deficiency. Small
 * multiples trade the ability to compare exact values across dimensions (which
 * nobody does) for the ability to read each dimension's SHAPE at a glance
 * (which is the actual question: which of these is heading the wrong way).
 *
 * Every panel shares one y-axis (1-5, the rubric's native scale) so the panels
 * are comparable by eye, and carries the soft-call reference line so "is this
 * bad" is answered without reading the axis.
 */
const DimensionTrendGrid: React.FC<DimensionTrendGridProps> = ({ history }) => {
  const series = useMemo(
    () =>
      (history ?? []).map((point, index) => ({
        index: index + 1,
        label: point.date
          ? new Date(point.date).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric'
            })
          : `Call ${index + 1}`,
        title: point.title,
        ...point.dimensions
      })),
    [history]
  );

  if (!series.length) {
    return null;
  }

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', mb: 3 }}>
      <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
        <Typography variant="subtitle2" fontWeight={700}>
          Every dimension over time
        </Typography>
        <Typography variant="caption" color="text.secondary">
          one panel per dimension · {series.length} scored{' '}
          {series.length === 1 ? 'call' : 'calls'} · dashed line is the{' '}
          {WEAK_AREA_THRESHOLD}% soft-call mark
        </Typography>
      </Box>

      <Box
        display="grid"
        gap={2}
        mt={2}
        sx={{
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(5, 1fr)'
          }
        }}
      >
        {RUBRIC_DIMENSIONS.map((dimension) => (
          <DimensionPanel
            key={dimension.key}
            dimensionKey={dimension.key}
            label={dimension.label}
            series={series}
          />
        ))}
      </Box>
    </Paper>
  );
};

const DimensionPanel: React.FC<{
  dimensionKey: string;
  label: string;
  series: Array<Record<string, any>>;
}> = ({ dimensionKey, label, series }) => {
  const scores = series
    .map((s) => s[dimensionKey])
    .filter((v): v is number => typeof v === 'number');

  const average = scores.length
    ? scores.reduce((a, b) => a + b, 0) / scores.length
    : null;
  const isWeak = average != null && average < THRESHOLD_SCORE;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.25,
        // The only colour signal in the grid: a weak panel gets a left rail.
        // Paired with the "below the line" caption so it is never colour alone.
        borderLeft: 3,
        borderLeftColor: isWeak ? CHART_INK.reference : 'divider'
      }}
    >
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ display: 'block', lineHeight: 1.3, minHeight: 32 }}
      >
        {label}
      </Typography>

      <Box display="flex" alignItems="baseline" gap={0.75}>
        <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {average != null ? average.toFixed(1) : '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          / {RUBRIC_MAX_SCORE}
        </Typography>
        {isWeak && (
          <Typography variant="caption" sx={{ color: CHART_INK.reference, ml: 'auto' }}>
            below the line
          </Typography>
        )}
      </Box>

      <Box height={64} mt={0.5}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
            <XAxis dataKey="label" hide />
            <YAxis
              domain={[1, RUBRIC_MAX_SCORE]}
              ticks={[1, 3, 5]}
              tick={{ fontSize: 9, fill: CHART_INK.muted }}
              axisLine={false}
              tickLine={false}
              width={28}
            />
            <ReferenceLine
              y={THRESHOLD_SCORE}
              stroke={CHART_INK.reference}
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <RechartsTooltip
              contentStyle={{ fontSize: 12, borderRadius: 6 }}
              formatter={(value: any) => [`${value} / ${RUBRIC_MAX_SCORE}`, label]}
              labelFormatter={(_: any, payload: any) =>
                payload?.[0]?.payload?.title || payload?.[0]?.payload?.label || ''
              }
            />
            <Line
              type="monotone"
              dataKey={dimensionKey}
              stroke={SERIES_COLOR}
              strokeWidth={2}
              dot={{ r: 2.5, strokeWidth: 0, fill: SERIES_COLOR }}
              activeDot={{ r: 4 }}
              connectNulls
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default DimensionTrendGrid;
